<template>
    <div class="pacientes-panel">
        <header class="pacientes-header">
            <div class="header-icon" aria-hidden="true">
                <i class="fas fa-users"></i>
            </div>
            <div class="header-text">
                <h6>Listado de pacientes</h6>
                <p v-if="exigeConsulta">Requiere una consulta atendida en los últimos 30 días</p>
                <p v-else>Selecciona uno para continuar</p>
            </div>
            <span v-if="!loading && !error" class="count-badge">{{ filtrados.length }}</span>
        </header>

        <div class="search-box">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
                v-model="busqueda"
                type="text"
                placeholder="Buscar por nombre o cédula"
                autocomplete="off"
                :disabled="loading"
            />
            <button
                v-if="busqueda"
                type="button"
                class="btn-clear"
                aria-label="Limpiar búsqueda"
                @click="busqueda = ''"
            >
                <i class="fas fa-times"></i>
            </button>
        </div>

        <div v-if="loading" class="pacientes-list" aria-hidden="true">
            <div v-for="n in 4" :key="'sk-' + n" class="paciente-card is-skeleton">
                <div class="sk sk-avatar"></div>
                <div class="sk-lines">
                    <div class="sk sk-line w-70"></div>
                    <div class="sk sk-line w-45"></div>
                </div>
            </div>
        </div>

        <div v-else-if="error" class="estado">
            <i class="fas fa-exclamation-circle"></i>
            <p>{{ error }}</p>
            <button type="button" class="btn-reintentar" @click="loadPacientesAll">Reintentar</button>
        </div>

        <div v-else-if="filtrados.length === 0" class="estado">
            <i class="fas fa-user-slash"></i>
            <p v-if="busqueda">No hay coincidencias para «{{ busqueda }}».</p>
            <p v-else>No hay pacientes registrados.</p>
        </div>

        <ul v-else class="pacientes-list">
            <li v-for="(paciente, index) in paginados" :key="paciente.cedula + '-' + index">
                <button type="button" class="paciente-card" :class="{ 'sin-consulta': exigeConsulta && !paciente.consulta_reciente }" @click="seleccionar(paciente)">
                    <span class="avatar" aria-hidden="true">{{ iniciales(paciente.nombres_pacientes) }}</span>
                    <span class="paciente-info">
                        <span class="paciente-nombre">{{ paciente.nombres_pacientes }}</span>
                        <span class="paciente-meta">
                            <span>Cédula {{ paciente.cedula }}</span>
                            <span v-if="edadDe(paciente.nacimiento) !== null">{{ edadDe(paciente.nacimiento) }} años</span>
                        </span>
                        <span v-if="exigeConsulta && !paciente.consulta_reciente" class="sin-consulta-nota">Sin consulta atendida</span>
                    </span>
                    <span class="btn-elegir" aria-hidden="true">
                        <i class="fas fa-plus"></i>
                    </span>
                </button>
            </li>
        </ul>

        <footer v-if="!loading && !error && filtrados.length > 0" class="pacientes-footer">
            <span class="rango">{{ rangoTexto }}</span>
            <div v-if="totalPaginas > 1" class="paginacion">
                <button type="button" :disabled="paginaVisible === 1" @click="paginaActual--">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <span>{{ paginaVisible }} / {{ totalPaginas }}</span>
                <button type="button" :disabled="paginaVisible === totalPaginas" @click="paginaActual++">
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
export default {
    name: "PacientesGeneral",

    props: {
        paraCertificado: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: true,
            error: "",
            busqueda: "",
            paginaActual: 1,
            porPagina: 6,
            isMobile: this.$isMobile,
            transactions: [],
            validarConsulta: true,
        };
    },

    computed: {
        filtrados() {
            const texto = String(this.busqueda || "").trim();
            const consulta = this.normalizar(texto);
            if (!consulta) return this.transactions;

            const palabras = consulta.split(/\s+/).filter(Boolean);
            return this.transactions.filter((paciente) => {
                const nombre = this.normalizar(paciente.nombres_pacientes);
                const cedula = String(paciente.cedula || "");
                if (cedula.includes(texto)) return true;
                return palabras.every((palabra) => nombre.includes(palabra));
            });
        },
        totalPaginas() {
            return Math.max(1, Math.ceil(this.filtrados.length / this.porPagina));
        },
        paginaVisible() {
            return Math.min(this.paginaActual, this.totalPaginas);
        },
        paginados() {
            const inicio = (this.paginaVisible - 1) * this.porPagina;
            return this.filtrados.slice(inicio, inicio + this.porPagina);
        },
        rangoTexto() {
            const total = this.filtrados.length;
            const inicio = (this.paginaVisible - 1) * this.porPagina + 1;
            const fin = Math.min(this.paginaVisible * this.porPagina, total);
            return `${inicio}–${fin} de ${total}`;
        },
        exigeConsulta() {
            return this.paraCertificado && this.validarConsulta;
        },
    },

    watch: {
        busqueda() {
            this.paginaActual = 1;
        },
        filtrados() {
            if (this.paginaActual > this.totalPaginas) {
                this.paginaActual = this.totalPaginas;
            }
        },
    },

    mounted() {
        if (this.isMobile) {
            this.$eventBus.emit("activeShowMenu");
        }
        this.loadPacientesAll();
    },

    methods: {
        normalizar(texto) {
            return String(texto || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();
        },

        loadPacientesAll() {
            this.loading = true;
            this.error = "";

            this.$https.post("/evaluacion/getLoadPacientesReceta", {}, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: this.tokenAuth,
                },
            }).then((response) => {
                if (response.data.code === 200) {
                    this.validarConsulta = response.data.validar_consulta !== false;
                    this.transactions = response.data.datos || [];
                    this.loading = false;
                    return;
                }

                this.loading = false;
                this.error = response.data.message || "No se pudo cargar el listado.";
            }).catch((error) => {
                this.loading = false;

                if (error?.response?.status === 400) {
                    this.error = error.response.data?.message || "No se pudo cargar el listado.";
                    this.$swal.fire({
                        title: "Ocurrió algo!",
                        html: `<p style="color: #eb1265; font-weight: bold; font-size: 13px;">${error.response.data.message}</p>`,
                        icon: "warning",
                        width: "400px",
                        customClass: {
                            confirmButton: "btn btn-primary",
                            cancelButton: "btn btn-outline-secondary ml-1",
                        },
                        buttonsStyling: false,
                    });
                } else if (error?.response?.status === 401) {
                    this.error = "La sesión expiró.";
                    this.showSessionExpiredAlert();
                } else {
                    this.error = "No se pudo cargar el listado.";
                }
            });
        },

        seleccionar(paciente) {
            if (this.exigeConsulta && !paciente.consulta_reciente) {
                this.$swal.fire({
                    title: "Imposible generar un certificado",
                    html: `<p style="color: #eb1265; font-weight: bold; font-size: 13px;">No existe una consulta atendida en los últimos 30 días.</p>`,
                    icon: "warning",
                    width: "400px",
                    customClass: {
                        confirmButton: "btn btn-primary",
                    },
                    buttonsStyling: false,
                });
                return;
            }

            this.$emit("datos-seleccionados", paciente);
            this.$emit("datos-seleccionados2", paciente);
            this.$emit("datos-seleccionados3", paciente);
        },

        iniciales(nombre) {
            const partes = String(nombre || "").trim().split(/\s+/).filter(Boolean);
            if (!partes.length) return "?";
            return partes.slice(0, 2).map((parte) => parte[0]).join("").toUpperCase();
        },

        edadDe(fecha) {
            if (!fecha) return null;
            const nacimiento = new Date(fecha);
            if (Number.isNaN(nacimiento.getTime())) return null;

            const hoy = new Date();
            let edad = hoy.getFullYear() - nacimiento.getFullYear();
            const mes = hoy.getMonth() - nacimiento.getMonth();
            if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
                edad -= 1;
            }
            return edad >= 0 ? edad : null;
        },
    },
};
</script>

<style scoped>
.pacientes-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: #1f2a37;
}

.pacientes-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 2px 8px;
    border-bottom: 1px solid #e6edf5;
    color: #1f2a37;
}

.header-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: #e8f4fc;
    color: #0078be;
    flex-shrink: 0;
}

.header-text {
    min-width: 0;
}

.header-text h6 {
    margin: 0;
    color: #1f2a37;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.2px;
}

.header-text p {
    margin: 2px 0 0;
    font-size: 0.75rem;
    color: #6b7c8f;
}

.count-badge {
    margin-left: auto;
    min-width: 28px;
    padding: 4px 10px;
    border-radius: 999px;
    background: #e8f4fc;
    color: #0078be;
    font-size: 0.75rem;
    font-weight: 700;
    text-align: center;
}

.search-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #e3eaf2;
    border-radius: 12px;
    background: #f4f7fb;
    color: #6b7c8f;
}

.search-box:focus-within {
    border-color: #0078be;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0, 120, 190, 0.12);
}

.search-box input {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    color: #1f2a37;
    font-size: 0.85rem;
}

.search-box input::placeholder {
    color: #8b9aab;
}

.btn-clear {
    border: 0;
    background: transparent;
    color: #8b9aab;
    padding: 0;
    line-height: 1;
}

.pacientes-list {
    list-style: none;
    margin: 0;
    padding: 2px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 340px;
    overflow-y: auto;
}

.pacientes-list::-webkit-scrollbar {
    width: 6px;
}

.pacientes-list::-webkit-scrollbar-thumb {
    background: #c5d4e2;
    border-radius: 99px;
}

.paciente-card {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e6edf5;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

button.paciente-card:hover {
    border-color: #0078be;
    box-shadow: 0 6px 16px rgba(0, 120, 190, 0.12);
}

button.paciente-card:focus-visible {
    outline: 2px solid #0078be;
    outline-offset: 2px;
}

.avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    background: #e8f4fc;
    color: #0078be;
    font-size: 0.72rem;
    font-weight: 700;
}

.paciente-info {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.paciente-nombre {
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.25;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.paciente-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: #6b7c8f;
    font-size: 0.72rem;
}

.sin-consulta {
    border-color: #f3d2dc;
    background: #fff8fa;
}

.sin-consulta-nota {
    color: #eb1265;
    font-size: 0.7rem;
    font-weight: 600;
}

.btn-elegir {
    margin-left: auto;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    background: #eb1265;
    color: #fff;
    font-size: 0.75rem;
}

button.paciente-card:hover .btn-elegir {
    background: #c4125e;
}

.pacientes-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #6b7c8f;
    font-size: 0.75rem;
}

.paginacion {
    display: flex;
    align-items: center;
    gap: 8px;
}

.paginacion button {
    width: 28px;
    height: 28px;
    border: 1px solid #e3eaf2;
    border-radius: 8px;
    background: #fff;
    color: #0078be;
}

.paginacion button:disabled {
    color: #c5d0db;
    cursor: not-allowed;
}

.estado {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 28px 12px;
    border: 1px dashed #d5e0ea;
    border-radius: 12px;
    color: #6b7c8f;
    text-align: center;
}

.estado i {
    font-size: 1.4rem;
    color: #0078be;
}

.estado p {
    margin: 0;
    font-size: 0.85rem;
}

.btn-reintentar {
    border: 0;
    border-radius: 8px;
    padding: 6px 14px;
    background: #0078be;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
}

.is-skeleton {
    cursor: default;
}

.sk {
    background: linear-gradient(90deg, #eef2f6 25%, #f8fafc 50%, #eef2f6 75%);
    background-size: 400px 100%;
    animation: shimmer 1.2s infinite linear;
}

.sk-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    flex-shrink: 0;
}

.sk-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-line {
    height: 10px;
    border-radius: 99px;
}

.w-70 { width: 70%; }
.w-45 { width: 45%; }

@keyframes shimmer {
    0% { background-position: -200px 0; }
    100% { background-position: 200px 0; }
}
</style>
